import { defineMessages, plural } from "../i18n/core";

export const adminStorageMessages = defineMessages({
  "pt-BR": {
    title: "Armazenamento da plataforma",
    lede: "Os pools são da plataforma. O cliente recebe volume por atribuição, com quota, e nunca vê backend, endereço ou credencial.",
    tabs: "Seções do armazenamento da plataforma",
    tabPools: "Pools",
    tabAssignments: "Atribuições",
    forbidden: "Esta área é do administrador da plataforma.",

    poolsSub: "Cada pool guarda blocos cifrados de várias organizações.",
    newPool: "Novo pool",
    noPools: "Nenhum pool cadastrado ainda.",
    primary: "Primário",
    replica: "Réplica",
    capacityInformed: "capacidade informada",
    capacityField: "Capacidade contratada (GB)",
    capacityPlaceholder: "ex.: 2000",
    capacityHint: "Quanto este destino tem, para comparar com as quotas dos clientes. Em disco local e SFTP o servidor também mede sozinho.",
    capacityDetected: "medida no destino",
    used: "usado",
    assignedQuota: "quota atribuída",
    orgs: "organizações",
    noLimit: "sem limite",
    notMeasured: "não medida",
    unknown: "—",
    freeAtDestination: "livre no destino",
    probedAt: (when: string) => `Capacidade medida ${when}.`,
    neverProbed: "Capacidade nunca medida neste destino.",
    measure: "Medir capacidade",
    measuring: "Medindo…",
    measured: (cap: string, free: string) => `Capacidade medida: ${cap}, ${free} livres.`,
    measuredNone: "Este backend não informa capacidade; vale o número informado.",
    overcommitted: (quota: string, capacity: string) =>
      `As quotas atribuídas somam ${quota}, acima da capacidade de ${capacity}. Não bloqueia ninguém, mas o pool pode encher antes de todos usarem o contratado.`,
    lastError: (msg: string) => `Última verificação falhou: ${msg}`,
    replicatedTo: "Replicado para",
    replicationLag: (lag: string) => `atraso de ${lag}`,
    localReadOnly: "Pools de disco local são configurados no servidor; aqui só medição e redundância.",
    capacityFromServer: "A capacidade informada vem da configuração do pool no servidor. Enquanto não houver uma, vale a medida no destino.",
    edit: "Editar",
    remove: "Remover",
    removeTitle: (name: string) => `Remover o pool ${name}?`,
    removeDescription: "Só é possível remover pools sem atribuição. Os dados no destino não são apagados.",
    removed: "Pool removido.",

    setRedundancy: "Configurar redundância",
    changeRedundancy: "Alterar redundância",
    redundancyTitle: (name: string) => `Redundância de ${name}`,
    redundancyDescription: "O servidor copia os blocos cifrados deste pool para a réplica a cada 10 minutos. Blocos apagados no primário ficam 7 dias na réplica antes de sair.",
    redundancyPool: "Pool de réplica",
    noRedundancy: "Sem redundância",
    redundancySaved: "Redundância salva.",

    dialogTitle: "Novo pool de armazenamento",
    editTitle: (name: string) => `Editar ${name}`,
    dialogDescription: "O servidor testa gravação, leitura, listagem e exclusão antes de salvar; as credenciais ficam cifradas e nunca chegam aos agentes.",
    comingSoon: "Azure Blob e Google Cloud Storage chegam em breve. Backblaze B2, Wasabi e Cloudflare R2 funcionam pela opção S3.",
    testConnection: "Testar conexão",
    testing: "Testando…",
    testOk: "Conexão testada: gravação, leitura, listagem e exclusão funcionaram.",
    save: "Salvar",
    saving: "Salvando…",
    created: "Pool criado.",
    updated: "Pool alterado.",
    failed: "Não foi possível concluir. Tente de novo.",
    type: "Tipo",
    name: "Nome",
    defaultName: "Novo pool",
    role: "Uso",
    rolePrimary: "Primário (recebe backups)",
    roleReplica: "Réplica (cópia de redundância)",
    roleTrial: "Teste gratuito (só para cadastros de teste)",
    trial: "Teste gratuito",
    trialLimits: "Limites de cada teste",
    trialLimitsHint: "Valem para os testes cadastrados a partir de agora neste pool. Os cadastros escolhem o pool de teste com mais espaço livre.",
    trialQuota: "Espaço por teste (GB)",
    trialMachines: "Máquinas por teste",
    trialDays: "Duração (dias)",
    trialGrace: "Dias para restaurar depois do fim",
    trialCapacityHint: "A capacidade contratada do pool dividida pelo espaço por teste diz quantos testes cabem ao mesmo tempo. Cheio, o cadastro avisa que os testes estão esgotados.",
    s3Endpoint: "Endpoint",
    s3EndpointHint: "Só o host, sem https://. Ex.: s3.sa-east-1.amazonaws.com, s3.us-west-004.backblazeb2.com",
    region: "Região",
    bucket: "Bucket",
    prefix: "Prefixo (opcional)",
    accessKey: "Chave de acesso",
    secret: "Chave secreta",
    keepSecret: "Em branco mantém a atual",
    sftpHost: "Servidor",
    port: "Porta",
    user: "Usuário",
    sftpPath: "Pasta",
    password: "Senha",
    privateKey: "Chave privada (opcional, em vez da senha)",
    hostKey: "Chave do servidor",
    fetchHostKey: "Buscar chave do servidor",
    hostKeyFingerprint: (type: string, fp: string) => `Impressão digital da chave ${type}: ${fp}`,
    hostKeyConfirm: "Confirmo que a impressão digital confere com a do servidor",
    hostKeyHint: "Compare com o que o administrador do servidor informar (ssh-keygen -lf, do mesmo tipo de chave). Sem essa confirmação a conexão fica exposta a interceptação.",
    webdavUrl: "Endereço",
    interserverIntro: "Storage hosting da InterServer pela API do servidor de armazenamento (beta). Crie uma conta FTP no DirectAdmin só para o backup: a API fica restrita à pasta inicial dela.",
    interserverUrl: "Endereço da API",
    interserverUrlHint: "https://storageNNNN.is.cc:8080, sem pasta: o nome do servidor de armazenamento (o que aparece no certificado), não o domínio da conta. Com o domínio da conta a conexão funciona, mas o certificado não confere.",
    interserverUser: "Conta FTP",
    interserverUserHint: "Usuário e senha da conta FTP criada no DirectAdmin (FTP Management).",
    interserverPath: "Pasta dos repositórios",
    interserverPathHint: "Dentro da pasta inicial da conta FTP; é criada se não existir.",
    serverCertificate: "Certificado do servidor",
    fetchCertificate: "Ver certificado do servidor",
    certSubject: (subject: string, issuer: string) => `Emitido para ${subject}, por ${issuer}.`,
    certValidity: (from: string, to: string) => `Válido de ${from} a ${to}`,
    certValid: "a verificação normal aceita; não precisa fixar.",
    certInvalid: (why: string) => `a verificação normal recusa (${why})`,
    certTrust: "Confiar neste certificado pela impressão digital, mesmo vencido ou autoassinado",
    certPinned: (prefix: string) => `Certificado fixado (SHA-256 ${prefix}…). Desmarque para voltar à verificação normal.`,
    certHint: "A impressão digital só é usada enquanto o certificado do servidor não for válido. Quando o provedor renovar o certificado, o pool segue conectando pela verificação normal. Se o servidor trocar por outro certificado inválido, a conexão para até você fixar o novo.",

    assignmentsSub: "Escolha a organização para ver e mudar o contratado dela.",
    organization: "Organização",
    assignPool: "Atribuir pool",
    noAssignments: "Nenhum pool atribuído: esta organização não consegue guardar backup.",
    colPool: "Pool",
    colQuota: "Contratado",
    colUsed: "Usado",
    colFree: "Livre",
    colMachines: "Máquinas",
    colActions: "Ações",
    machines: (n: number) => `${n} ${plural(n, "máquina", "máquinas")}`,
    draining: "Em esvaziamento",
    changeQuota: "Alterar",
    quotaTitle: (name: string) => `Contratado em ${name}`,
    quotaDescription: "A quota é teto: ao atingir 100% o servidor recusa blocos novos e a restauração continua funcionando. Em branco significa sem limite.",
    quotaField: "Quota (GB)",
    quotaNoLimit: "Sem limite",
    quotaSaved: "Contratado gravado.",
    assignTitle: (org: string) => `Atribuir pool a ${org}`,
    assignDescription: "A máquina cai no pool atribuído com mais espaço livre de quota; o cliente não escolhe.",
    assignPoolField: "Pool",
    assignNoneLeft: "Todos os pools já estão atribuídos a esta organização.",
    assigned: "Pool atribuído.",
    removeAssignment: "Remover",
    removeAssignmentTitle: (name: string) => `Remover ${name} desta organização?`,
    removeAssignmentDescription: "Só é possível remover uma atribuição sem dados. A troca de pool com cópia dos dados chega na próxima entrega.",
    assignmentRemoved: "Atribuição removida.",
  },
  "en-US": {
    title: "Platform storage",
    lede: "Pools belong to the platform. Customers get volume through an assignment, with a quota, and never see backend, address or credentials.",
    tabs: "Platform storage sections",
    tabPools: "Pools",
    tabAssignments: "Assignments",
    forbidden: "This area belongs to the platform administrator.",

    poolsSub: "Each pool stores encrypted blocks from several organizations.",
    newPool: "New pool",
    noPools: "No pool registered yet.",
    primary: "Primary",
    replica: "Replica",
    capacityInformed: "informed capacity",
    capacityField: "Contracted capacity (GB)",
    capacityPlaceholder: "e.g. 2000",
    capacityHint: "How much this destination holds, to compare against customer quotas. On local disk and SFTP the server also measures it.",
    capacityDetected: "measured at the destination",
    used: "used",
    assignedQuota: "assigned quota",
    orgs: "organizations",
    noLimit: "no limit",
    notMeasured: "not measured",
    unknown: "—",
    freeAtDestination: "free at the destination",
    probedAt: (when: string) => `Capacity measured ${when}.`,
    neverProbed: "Capacity never measured at this destination.",
    measure: "Measure capacity",
    measuring: "Measuring…",
    measured: (cap: string, free: string) => `Measured capacity: ${cap}, ${free} free.`,
    measuredNone: "This backend does not report capacity; the informed number applies.",
    overcommitted: (quota: string, capacity: string) =>
      `Assigned quotas add up to ${quota}, above the ${capacity} capacity. Nothing is blocked, but the pool may fill up before everyone uses what they contracted.`,
    lastError: (msg: string) => `Last check failed: ${msg}`,
    replicatedTo: "Replicated to",
    replicationLag: (lag: string) => `${lag} behind`,
    localReadOnly: "Local disk pools are configured on the server; here you can only measure and set redundancy.",
    capacityFromServer: "The informed capacity comes from the pool configuration on the server. Until there is one, the measured value applies.",
    edit: "Edit",
    remove: "Remove",
    removeTitle: (name: string) => `Remove pool ${name}?`,
    removeDescription: "Only pools with no assignment can be removed. Data at the destination is not deleted.",
    removed: "Pool removed.",

    setRedundancy: "Set up redundancy",
    changeRedundancy: "Change redundancy",
    redundancyTitle: (name: string) => `Redundancy for ${name}`,
    redundancyDescription: "The server copies this pool's encrypted blocks to the replica every 10 minutes. Blocks deleted on the primary stay on the replica for 7 days before being removed.",
    redundancyPool: "Replica pool",
    noRedundancy: "No redundancy",
    redundancySaved: "Redundancy saved.",

    dialogTitle: "New storage pool",
    editTitle: (name: string) => `Edit ${name}`,
    dialogDescription: "The server tests writing, reading, listing and deleting before saving; credentials are encrypted and never reach the agents.",
    comingSoon: "Azure Blob and Google Cloud Storage are coming soon. Backblaze B2, Wasabi and Cloudflare R2 work through the S3 option.",
    testConnection: "Test connection",
    testing: "Testing…",
    testOk: "Connection tested: write, read, list and delete worked.",
    save: "Save",
    saving: "Saving…",
    created: "Pool created.",
    updated: "Pool changed.",
    failed: "Could not complete. Try again.",
    type: "Type",
    name: "Name",
    defaultName: "New pool",
    role: "Use",
    rolePrimary: "Primary (receives backups)",
    roleReplica: "Replica (redundancy copy)",
    roleTrial: "Free trial (trial sign-ups only)",
    trial: "Free trial",
    trialLimits: "Limits of each trial",
    trialLimitsHint: "They apply to trials signed up from now on in this pool. Sign-ups pick the trial pool with the most free space.",
    trialQuota: "Space per trial (GB)",
    trialMachines: "Machines per trial",
    trialDays: "Duration (days)",
    trialGrace: "Days to restore after the end",
    trialCapacityHint: "The pool's contracted capacity divided by the space per trial tells how many trials fit at once. When full, the sign-up says trials are sold out.",
    s3Endpoint: "Endpoint",
    s3EndpointHint: "Host only, without https://. E.g. s3.us-east-1.amazonaws.com, s3.us-west-004.backblazeb2.com",
    region: "Region",
    bucket: "Bucket",
    prefix: "Prefix (optional)",
    accessKey: "Access key",
    secret: "Secret key",
    keepSecret: "Leave blank to keep the current one",
    sftpHost: "Server",
    port: "Port",
    user: "Username",
    sftpPath: "Folder",
    password: "Password",
    privateKey: "Private key (optional, instead of the password)",
    hostKey: "Server host key",
    fetchHostKey: "Fetch server key",
    hostKeyFingerprint: (type: string, fp: string) => `Fingerprint of the ${type} key: ${fp}`,
    hostKeyConfirm: "I confirm the fingerprint matches the server's",
    hostKeyHint: "Compare it with what the server administrator reports (ssh-keygen -lf, same key type). Without this confirmation the connection is exposed to interception.",
    webdavUrl: "Address",
    interserverIntro: "InterServer storage hosting through the storage server's API (beta). Create an FTP account in DirectAdmin just for backups: the API is restricted to its home folder.",
    interserverUrl: "API address",
    interserverUrlHint: "https://storageNNNN.is.cc:8080, without a folder: the storage server name (the one in its certificate), not the account domain. The account domain connects, but the certificate does not match it.",
    interserverUser: "FTP account",
    interserverUserHint: "User and password of the FTP account created in DirectAdmin (FTP Management).",
    interserverPath: "Repositories folder",
    interserverPathHint: "Inside the FTP account's home folder; created if it does not exist.",
    serverCertificate: "Server certificate",
    fetchCertificate: "View server certificate",
    certSubject: (subject: string, issuer: string) => `Issued to ${subject}, by ${issuer}.`,
    certValidity: (from: string, to: string) => `Valid from ${from} to ${to}`,
    certValid: "normal verification accepts it; no need to pin.",
    certInvalid: (why: string) => `normal verification rejects it (${why})`,
    certTrust: "Trust this certificate by fingerprint, even if expired or self-signed",
    certPinned: (prefix: string) => `Certificate pinned (SHA-256 ${prefix}…). Untick to go back to normal verification.`,
    certHint: "The fingerprint is only used while the server certificate is not valid. When the provider renews the certificate, the pool keeps connecting through normal verification. If the server switches to another invalid certificate, the connection stops until you pin the new one.",

    assignmentsSub: "Pick the organization to see and change what it has contracted.",
    organization: "Organization",
    assignPool: "Assign pool",
    noAssignments: "No pool assigned: this organization cannot store backups.",
    colPool: "Pool",
    colQuota: "Contracted",
    colUsed: "Used",
    colFree: "Free",
    colMachines: "Machines",
    colActions: "Actions",
    machines: (n: number) => `${n} ${plural(n, "machine", "machines")}`,
    draining: "Draining",
    changeQuota: "Change",
    quotaTitle: (name: string) => `Contracted on ${name}`,
    quotaDescription: "The quota is a ceiling: at 100% the server refuses new blocks and restores keep working. Blank means no limit.",
    quotaField: "Quota (GB)",
    quotaNoLimit: "No limit",
    quotaSaved: "Contracted volume saved.",
    assignTitle: (org: string) => `Assign a pool to ${org}`,
    assignDescription: "A machine lands on the assigned pool with the most quota space free; the customer does not choose.",
    assignPoolField: "Pool",
    assignNoneLeft: "Every pool is already assigned to this organization.",
    assigned: "Pool assigned.",
    removeAssignment: "Remove",
    removeAssignmentTitle: (name: string) => `Remove ${name} from this organization?`,
    removeAssignmentDescription: "Only an assignment with no data can be removed. Switching pools with a copy of the data comes in the next release.",
    assignmentRemoved: "Assignment removed.",
  },
  es: {
    title: "Almacenamiento de la plataforma",
    lede: "Los pools son de la plataforma. El cliente recibe volumen por asignación, con cuota, y nunca ve backend, dirección ni credenciales.",
    tabs: "Secciones del almacenamiento de la plataforma",
    tabPools: "Pools",
    tabAssignments: "Asignaciones",
    forbidden: "Esta área es del administrador de la plataforma.",

    poolsSub: "Cada pool guarda bloques cifrados de varias organizaciones.",
    newPool: "Nuevo pool",
    noPools: "Todavía no hay ningún pool registrado.",
    primary: "Principal",
    replica: "Réplica",
    capacityInformed: "capacidad informada",
    capacityField: "Capacidad contratada (GB)",
    capacityPlaceholder: "ej.: 2000",
    capacityHint: "Cuánto tiene este destino, para comparar con las cuotas de los clientes. En disco local y SFTP el servidor también la mide.",
    capacityDetected: "medida en el destino",
    used: "usado",
    assignedQuota: "cuota asignada",
    orgs: "organizaciones",
    noLimit: "sin límite",
    notMeasured: "no medida",
    unknown: "—",
    freeAtDestination: "libre en el destino",
    probedAt: (when: string) => `Capacidad medida ${when}.`,
    neverProbed: "La capacidad nunca se midió en este destino.",
    measure: "Medir capacidad",
    measuring: "Midiendo…",
    measured: (cap: string, free: string) => `Capacidad medida: ${cap}, ${free} libres.`,
    measuredNone: "Este backend no informa la capacidad; vale el número informado.",
    overcommitted: (quota: string, capacity: string) =>
      `Las cuotas asignadas suman ${quota}, por encima de la capacidad de ${capacity}. No bloquea a nadie, pero el pool puede llenarse antes de que todos usen lo contratado.`,
    lastError: (msg: string) => `La última verificación falló: ${msg}`,
    replicatedTo: "Replicado en",
    replicationLag: (lag: string) => `retraso de ${lag}`,
    localReadOnly: "Los pools de disco local se configuran en el servidor; aquí solo medición y redundancia.",
    capacityFromServer: "La capacidad informada viene de la configuración del pool en el servidor. Mientras no haya una, vale la medida en el destino.",
    edit: "Editar",
    remove: "Quitar",
    removeTitle: (name: string) => `¿Quitar el pool ${name}?`,
    removeDescription: "Solo se pueden quitar pools sin asignación. Los datos en el destino no se borran.",
    removed: "Pool quitado.",

    setRedundancy: "Configurar redundancia",
    changeRedundancy: "Cambiar redundancia",
    redundancyTitle: (name: string) => `Redundancia de ${name}`,
    redundancyDescription: "El servidor copia los bloques cifrados de este pool a la réplica cada 10 minutos. Los bloques borrados en el principal permanecen 7 días en la réplica antes de eliminarse.",
    redundancyPool: "Pool de réplica",
    noRedundancy: "Sin redundancia",
    redundancySaved: "Redundancia guardada.",

    dialogTitle: "Nuevo pool de almacenamiento",
    editTitle: (name: string) => `Editar ${name}`,
    dialogDescription: "El servidor prueba escritura, lectura, listado y borrado antes de guardar; las credenciales se cifran y nunca llegan a los agentes.",
    comingSoon: "Azure Blob y Google Cloud Storage llegan pronto. Backblaze B2, Wasabi y Cloudflare R2 funcionan con la opción S3.",
    testConnection: "Probar conexión",
    testing: "Probando…",
    testOk: "Conexión probada: escritura, lectura, listado y borrado funcionaron.",
    save: "Guardar",
    saving: "Guardando…",
    created: "Pool creado.",
    updated: "Pool modificado.",
    failed: "No se pudo completar. Inténtalo de nuevo.",
    type: "Tipo",
    name: "Nombre",
    defaultName: "Nuevo pool",
    role: "Uso",
    rolePrimary: "Principal (recibe copias de seguridad)",
    roleReplica: "Réplica (copia de redundancia)",
    roleTrial: "Prueba gratuita (solo para registros de prueba)",
    trial: "Prueba gratuita",
    trialLimits: "Límites de cada prueba",
    trialLimitsHint: "Valen para las pruebas registradas desde ahora en este pool. Los registros eligen el pool de prueba con más espacio libre.",
    trialQuota: "Espacio por prueba (GB)",
    trialMachines: "Equipos por prueba",
    trialDays: "Duración (días)",
    trialGrace: "Días para restaurar después del fin",
    trialCapacityHint: "La capacidad contratada del pool dividida por el espacio por prueba indica cuántas pruebas caben a la vez. Lleno, el registro avisa que las pruebas están agotadas.",
    s3Endpoint: "Endpoint",
    s3EndpointHint: "Solo el host, sin https://. Ej.: s3.us-east-1.amazonaws.com, s3.us-west-004.backblazeb2.com",
    region: "Región",
    bucket: "Bucket",
    prefix: "Prefijo (opcional)",
    accessKey: "Clave de acceso",
    secret: "Clave secreta",
    keepSecret: "Déjalo en blanco para mantener la actual",
    sftpHost: "Servidor",
    port: "Puerto",
    user: "Usuario",
    sftpPath: "Carpeta",
    password: "Contraseña",
    privateKey: "Clave privada (opcional, en lugar de la contraseña)",
    hostKey: "Clave del servidor",
    fetchHostKey: "Obtener clave del servidor",
    hostKeyFingerprint: (type: string, fp: string) => `Huella digital de la clave ${type}: ${fp}`,
    hostKeyConfirm: "Confirmo que la huella digital coincide con la del servidor",
    hostKeyHint: "Compárala con lo que informe el administrador del servidor (ssh-keygen -lf, del mismo tipo de clave). Sin esta confirmación la conexión queda expuesta a interceptación.",
    webdavUrl: "Dirección",
    interserverIntro: "Storage hosting de InterServer a través de la API del servidor de almacenamiento (beta). Crea una cuenta FTP en DirectAdmin solo para la copia: la API queda restringida a su carpeta inicial.",
    interserverUrl: "Dirección de la API",
    interserverUrlHint: "https://storageNNNN.is.cc:8080, sin carpeta: el nombre del servidor de almacenamiento (el que figura en su certificado), no el dominio de la cuenta. Con el dominio de la cuenta conecta, pero el certificado no coincide.",
    interserverUser: "Cuenta FTP",
    interserverUserHint: "Usuario y contraseña de la cuenta FTP creada en DirectAdmin (FTP Management).",
    interserverPath: "Carpeta de los repositorios",
    interserverPathHint: "Dentro de la carpeta inicial de la cuenta FTP; se crea si no existe.",
    serverCertificate: "Certificado del servidor",
    fetchCertificate: "Ver certificado del servidor",
    certSubject: (subject: string, issuer: string) => `Emitido para ${subject}, por ${issuer}.`,
    certValidity: (from: string, to: string) => `Válido de ${from} a ${to}`,
    certValid: "la verificación normal lo acepta; no hace falta fijarlo.",
    certInvalid: (why: string) => `la verificación normal lo rechaza (${why})`,
    certTrust: "Confiar en este certificado por su huella, aunque esté vencido o sea autofirmado",
    certPinned: (prefix: string) => `Certificado fijado (SHA-256 ${prefix}…). Desmarca para volver a la verificación normal.`,
    certHint: "La huella solo se usa mientras el certificado del servidor no sea válido. Cuando el proveedor renueve el certificado, el pool sigue conectando por la verificación normal. Si el servidor cambia a otro certificado inválido, la conexión se detiene hasta fijar el nuevo.",

    assignmentsSub: "Elige la organización para ver y cambiar lo que tiene contratado.",
    organization: "Organización",
    assignPool: "Asignar pool",
    noAssignments: "Ningún pool asignado: esta organización no puede guardar copias.",
    colPool: "Pool",
    colQuota: "Contratado",
    colUsed: "Usado",
    colFree: "Libre",
    colMachines: "Equipos",
    colActions: "Acciones",
    machines: (n: number) => `${n} ${plural(n, "equipo", "equipos")}`,
    draining: "En vaciado",
    changeQuota: "Cambiar",
    quotaTitle: (name: string) => `Contratado en ${name}`,
    quotaDescription: "La cuota es un techo: al llegar al 100% el servidor rechaza bloques nuevos y la restauración sigue funcionando. En blanco significa sin límite.",
    quotaField: "Cuota (GB)",
    quotaNoLimit: "Sin límite",
    quotaSaved: "Contratado guardado.",
    assignTitle: (org: string) => `Asignar un pool a ${org}`,
    assignDescription: "El equipo cae en el pool asignado con más espacio libre de cuota; el cliente no elige.",
    assignPoolField: "Pool",
    assignNoneLeft: "Todos los pools ya están asignados a esta organización.",
    assigned: "Pool asignado.",
    removeAssignment: "Quitar",
    removeAssignmentTitle: (name: string) => `¿Quitar ${name} de esta organización?`,
    removeAssignmentDescription: "Solo se puede quitar una asignación sin datos. El cambio de pool con copia de los datos llega en la próxima entrega.",
    assignmentRemoved: "Asignación quitada.",
  },
});
